import { useEffect, useLayoutEffect } from 'react';
import Svg from '@components/Svg';
import { Item, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '@components/ui/item';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import { sanitizeHtml } from '@helpers/sanitizeHtml';
import type { SearchResult } from '@responses/search';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from 'cn';

import { StatusDot } from '../components/StatusDot';
import { TYPE_TOOLTIPS } from '../constants';
import { flattenHighlights } from '../utils/flatten-highlights';
import { formatUpdatedAt } from '../utils/format-updated-at';

export const SearchResults = ({
  parentRef,
  searchResults,
  hasNextPage,
  showUpdatedAt,
  activeIndex,
  fetchNextPage,
  isFetchingNextPage,
  setActiveIndex,
  onSelectItem,
}: {
  parentRef: React.RefObject<HTMLDivElement | null>;
  searchResults: SearchResult[];
  hasNextPage: boolean;
  showUpdatedAt: boolean;
  activeIndex: number;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  setActiveIndex: (index: number) => void;
  onSelectItem: (item: SearchResult, openInNewTab: boolean) => void;
}) => {
  'use memo';

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? searchResults.length + 1 : searchResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (idx) => {
      const result = searchResults[idx];
      if (!result) {
        return 50;
      }

      let height = 38;

      result.highlights?.forEach((highlight) => {
        highlight.fragments.forEach(() => {
          height += 24;
        });
      });

      return height;
    },
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualListHeight = rowVirtualizer.getTotalSize();

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [searchResults, rowVirtualizer]);

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= searchResults.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, virtualItems, searchResults.length]);

  return (
    <div
      ref={parentRef}
      style={{
        height: `500px`,
        width: `100%`,
        overflow: 'auto',
        padding: '4px',
      }}
    >
      <ItemGroup
        style={{
          height: `${virtualListHeight}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index > searchResults.length - 1;
          const item = searchResults[virtualRow.index];

          const fragments = flattenHighlights(item?.highlights ?? []); //.slice(0, MAX_HIGHLIGHTS_PER_RESULT);
          const updatedAt = showUpdatedAt ? formatUpdatedAt(item?.updated_at) : undefined;

          const isActive = virtualRow.index === activeIndex;

          return (
            <div
              key={virtualRow.index}
              style={{
                height: `${virtualRow.size}px`,
                width: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <Item>
                  <ItemContent className="items-center justify-center">
                    <ItemTitle>{hasNextPage ? 'Loading...' : 'No more results'}</ItemTitle>
                  </ItemContent>
                </Item>
              ) : (
                <Item
                  id={`command-palette-item-${virtualRow.index}`}
                  size="xs"
                  onMouseMove={() => setActiveIndex(virtualRow.index)}
                  onMouseDown={(event) => {
                    // mousedown (not click) so the input never loses focus
                    event.preventDefault();
                    onSelectItem(item, event.metaKey || event.ctrlKey);
                  }}
                  className={cn('hover:bg-muted cursor-pointer flex-nowrap transition-colors', isActive && 'bg-accent')}
                >
                  <ItemMedia variant="icon">
                    <Tooltip>
                      <TooltipTrigger>
                        <Svg name={item.type} />
                      </TooltipTrigger>
                      <TooltipContent className="z-5000">{TYPE_TOOLTIPS[item.type]}</TooltipContent>
                    </Tooltip>
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle className="w-full">
                      <span>{item.name || 'Untitled'}</span>
                      {item.ref && <span className="text-muted-foreground shrink-0 text-xs">{item.ref}</span>}
                      {item.umr && <span className="text-muted-foreground shrink-0 text-xs">{item.umr}</span>}

                      <span className="grow" />
                      {updatedAt && <span className="text-muted-foreground shrink-0 text-xs">{updatedAt}</span>}
                      {item.status && <StatusDot status={item.status} />}
                    </ItemTitle>
                    {fragments.map((fragment, index) => (
                      <ItemDescription key={index} className="text-muted-foreground w-9/10 truncate py-1 text-xs">
                        {fragment.blockName && item.type === 'submissions' && (
                          <span className="text-foreground/70 font-medium italic">{fragment.blockName} · </span>
                        )}
                        <span
                          className="[&_em]:text-foreground [&_em]:font-semibold [&_em]:not-italic"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(fragment.text) }}
                        />
                      </ItemDescription>
                    ))}
                  </ItemContent>
                </Item>
              )}
            </div>
          );
        })}
      </ItemGroup>
    </div>
  );
};
